import type { DemoLevel, DemoEndpointResult } from '../types/permissionDemo';
import { useDemoEndpoint } from '../hooks/useDemoEndpoint';

interface EndpointMeta {
  level: DemoLevel;
  title: string;
  guard: string;
  hint: string;
}

const ENDPOINTS: EndpointMeta[] = [
  {
    level: 'public',
    title: 'public (인증만)',
    guard: '(없음)',
    hint: '인증된 모든 사용자에게 공개. @HasPermission 없음.',
  },
  {
    level: 'user-only',
    title: 'user-only (USER_READ)',
    guard: '@HasPermission("USER_READ")',
    hint: 'USER_READ 권한 보유자만 접근. ROLE_USER 이상.',
  },
  {
    level: 'manager-only',
    title: 'manager-only (USER_WRITE | AUDIT_READ)',
    guard: '@HasAnyPermission({"USER_WRITE","AUDIT_READ"})',
    hint: 'USER_WRITE 또는 AUDIT_READ 권한 중 하나 이상. ROLE_MANAGER 이상.',
  },
  {
    level: 'admin-only',
    title: 'admin-only (GATEWAY_ADMIN)',
    guard: '@HasPermission("GATEWAY_ADMIN")',
    hint: 'GATEWAY_ADMIN 권한 필요. ROLE_ADMIN 전용.',
  },
];

function ResultCard({ result, level }: { result: DemoEndpointResult; level: DemoLevel }) {
  if (result.status === 'idle') {
    return (
      <div
        style={{
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-3)',
          border: '1px dashed var(--color-surface-border)',
          color: 'var(--color-text-muted)',
          fontStyle: 'italic',
          fontSize: 'var(--font-size-sm)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
        }}
        aria-label={`${level} 결과 대기 중`}
      >
        버튼을 눌러 호출해 보세요
      </div>
    );
  }

  if (result.status === 'loading') {
    return (
      <div
        aria-busy="true"
        aria-label={`${level} 호출 중`}
        style={{
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-3)',
          border: '1px solid var(--color-surface-border)',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--font-size-sm)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
        }}
      >
        <span
          style={{
            width: 14,
            height: 14,
            border: '2px solid var(--color-brand-primary)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'spin 0.7s linear infinite',
            flexShrink: 0,
          }}
          aria-hidden="true"
        />
        호출 중...
      </div>
    );
  }

  if (result.status === 'ok') {
    const d = result.data;
    const formattedTime = (() => {
      try {
        return new Date(d.calledAt).toLocaleTimeString('ko-KR', { timeZone: 'Asia/Seoul' });
      } catch {
        return d.calledAt;
      }
    })();

    return (
      <div
        role="status"
        aria-label={`${level} 200 OK`}
        style={{
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-2)',
          border: '1px solid var(--color-success)',
          background: 'color-mix(in srgb, var(--color-success) 10%, var(--color-surface-elevated))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 700,
              padding: '2px var(--spacing-2)',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-success)',
              color: 'var(--color-text-inverse)',
            }}
          >
            200 OK
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
            호출자: {d.calledBy}
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginLeft: 'auto' }}>
            {formattedTime}
          </span>
        </div>
        <pre
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
            background: 'var(--color-surface-background)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-sm)',
            padding: 'var(--spacing-2)',
            overflowX: 'auto',
            maxHeight: 130,
            overflowY: 'auto',
            margin: 0,
            whiteSpace: 'pre',
          }}
          aria-label="응답 본문"
        >
          {JSON.stringify({ endpoint: d.endpoint, message: d.message, callerRoles: d.callerRoles }, null, 2)}
        </pre>
      </div>
    );
  }

  if (result.status === 'forbidden') {
    return (
      <div
        role="status"
        aria-label={`${level} 403 예상된 결과`}
        style={{
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-3)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-2)',
          /* 403 = 정상 데모 결과 — 회색 (NOT red) */
          border: '1px solid var(--color-surface-border)',
          background: 'var(--color-surface-elevated)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 700,
              padding: '2px var(--spacing-2)',
              borderRadius: 'var(--radius-full)',
              background: 'var(--color-surface-muted)',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-text-muted)',
            }}
          >
            403
          </span>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
            예상된 결과 — 권한 게이트가 의도대로 동작하고 있습니다
          </span>
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          {result.code} · {result.message}
          {result.detail && ` · ${result.detail}`}
        </div>
      </div>
    );
  }

  // error
  return (
    <div
      role="alert"
      style={{
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-3)',
        border: '1px solid var(--color-danger)',
        background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-elevated))',
        color: 'var(--color-danger)',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      {result.message}
    </div>
  );
}

export function DemoEndpointsTab() {
  const { results, call, resetAll } = useDemoEndpoint();

  const anyLoading = Object.values(results).some((r) => r.status === 'loading');

  return (
    <div>
      {/* Header actions */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
        <button
          type="button"
          onClick={() => {
            for (const ep of ENDPOINTS) {
              call(ep.level);
            }
          }}
          disabled={anyLoading}
          style={{
            background: 'var(--color-brand-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            padding: 'var(--spacing-2) var(--spacing-4)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: anyLoading ? 'not-allowed' : 'pointer',
            opacity: anyLoading ? 0.5 : 1,
            fontFamily: 'var(--font-family-sans)',
          }}
          aria-label="4개 endpoint 전체 호출"
        >
          전체 호출
        </button>
        <button
          type="button"
          onClick={resetAll}
          style={{
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            color: 'var(--color-text-secondary)',
            padding: 'var(--spacing-2) var(--spacing-4)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-family-sans)',
          }}
          aria-label="모든 결과 초기화"
        >
          초기화
        </button>
      </div>

      {/* Endpoint cards grid — 2 columns on md+ */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 'var(--spacing-4)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        {ENDPOINTS.map((ep) => {
          const result = results[ep.level];
          const isLoading = result.status === 'loading';

          return (
            <div
              key={ep.level}
              style={{
                background: 'var(--color-surface-muted)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--spacing-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-3)',
              }}
            >
              {/* Card header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                    {/* Method badge */}
                    <span
                      style={{
                        display: 'inline-block',
                        fontFamily: 'var(--font-family-mono)',
                        fontSize: 10,
                        fontWeight: 700,
                        padding: '2px var(--spacing-2)',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--color-info)',
                        color: 'var(--color-text-inverse)',
                        letterSpacing: '0.05em',
                      }}
                      aria-label="HTTP 메서드: GET"
                    >
                      GET
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 700,
                        color: 'var(--color-text-primary)',
                      }}
                    >
                      {ep.title}
                    </span>
                  </div>
                  {/* URL path */}
                  <code
                    style={{
                      fontFamily: 'var(--font-family-mono)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      background: 'var(--color-surface-elevated)',
                      padding: '2px var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      display: 'inline-block',
                      marginTop: 'var(--spacing-1)',
                    }}
                  >
                    /api/eraf/permission-demo/{ep.level}
                  </code>
                </div>

                {/* Call button */}
                <button
                  type="button"
                  onClick={() => call(ep.level)}
                  disabled={isLoading}
                  aria-label={`${ep.level} endpoint 호출`}
                  aria-busy={isLoading}
                  style={{
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    opacity: isLoading ? 0.6 : 1,
                    fontFamily: 'var(--font-family-sans)',
                    flexShrink: 0,
                  }}
                >
                  호출
                </button>
              </div>

              {/* Guard annotation */}
              <code
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-brand-primary)',
                  background: 'color-mix(in srgb, var(--color-brand-primary) 10%, transparent)',
                  padding: '2px var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                  display: 'inline-block',
                  wordBreak: 'break-all',
                }}
                aria-label={`보안 어노테이션: ${ep.guard}`}
              >
                {ep.guard}
              </code>

              {/* Result area */}
              <ResultCard result={result} level={ep.level} />

              {/* Hint */}
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  lineHeight: 1.5,
                  paddingTop: 'var(--spacing-1)',
                  borderTop: '1px dashed var(--color-surface-border)',
                  margin: 0,
                }}
              >
                {ep.hint}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
